import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { env } from '@/lib/env';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PayTransactionStatus = 'succeeded' | 'failed' | 'refunded' | 'disputed';

export interface PayTransaction {
  id: string;
  created_at: string;
  amount: number;               // øre
  fee: number;                  // platform fee øre
  net: number;                  // amount - fee øre
  status: PayTransactionStatus;
  customer_email: string | null;
  description: string | null;
  invoice_id: string | null;    // v2 invoice UUID (from metadata)
  booking_id: string | null;    // v2 booking UUID (from metadata)
  stripe_receipt_url: string | null;
  currency: string;             // DKK at v1
}

export interface PayTransactionAggregates {
  total_volume: number;   // sum of amount for succeeded transactions (øre)
  total_fees: number;     // sum of fee for succeeded transactions (øre)
  count: number;          // total matching transactions
  refund_count: number;   // count of refunded transactions
}

export interface PayTransactionsFilters {
  since?: string;                     // ISO datetime
  until?: string;                     // ISO datetime
  status?: PayTransactionStatus[];
  limit?: number;                     // default 50, max 200
  cursor?: string;
}

export interface PayTransactionsResponse {
  items: PayTransaction[];
  cursor: string | null;
  has_more: boolean;
  aggregates: PayTransactionAggregates;
}

export interface MonthlyStatPoint {
  month: string;      // Short label e.g. "jan. '25"
  volume: number;     // Succeeded amount in øre
  fees: number;       // Succeeded fees in øre
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const payTransactionsQueryKey = (
  clinicId: string | null,
  filters: PayTransactionsFilters,
) => ['pay', 'transactions', clinicId, filters] as const;

export const payMonthlyStatsQueryKey = (clinicId: string | null) =>
  ['pay', 'monthly-stats', clinicId] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Build an authenticated fetch to a Supabase Edge Function. */
async function invokeEdgeFunctionGet<T>(
  functionName: string,
  params: Record<string, string | undefined>,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, v);
  }

  const resp = await fetch(
    `${env.VITE_SUPABASE_URL}/functions/v1/${functionName}?${qs.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session?.access_token ?? ''}`,
        apikey: env.VITE_SUPABASE_ANON_KEY,
      },
    },
  );

  const json = (await resp.json()) as { ok: boolean; data?: T; error?: { message: string } };
  if (!resp.ok || !json.ok) {
    throw new Error(json.error?.message ?? `${functionName} failed (${resp.status})`);
  }
  return json.data as T;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of transactions from Clinio Pay via the
 * pay-list-transactions Edge Function. Also returns aggregate totals for
 * the current filter window.
 */
export function usePayTransactions(
  clinicId: string | null | undefined,
  filters: PayTransactionsFilters = {},
) {
  return useQuery({
    queryKey: payTransactionsQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PayTransactionsResponse> => {
      if (!clinicId) {
        return {
          items: [],
          cursor: null,
          has_more: false,
          aggregates: { total_volume: 0, total_fees: 0, count: 0, refund_count: 0 },
        };
      }

      return invokeEdgeFunctionGet<PayTransactionsResponse>('pay-list-transactions', {
        clinic_id: clinicId,
        since:     filters.since,
        until:     filters.until,
        status:    filters.status && filters.status.length > 0
          ? filters.status.join(',')
          : undefined,
        limit:  filters.limit ? String(filters.limit) : undefined,
        cursor: filters.cursor,
      });
    },
    staleTime: 30_000,
  });
}

/**
 * Monthly volume + fees for the last 12 months via Clinio Pay (pay-list-transactions).
 */
export function usePayMonthlyStats(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: payMonthlyStatsQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<MonthlyStatPoint[]> => {
      if (!clinicId) return [];

      const since = new Date();
      since.setFullYear(since.getFullYear() - 1);
      since.setDate(1);
      since.setHours(0, 0, 0, 0);

      const data = await invokeEdgeFunctionGet<PayTransactionsResponse>(
        'pay-list-transactions',
        {
          clinic_id: clinicId,
          since: since.toISOString(),
          status: 'succeeded',
          limit: '200',
        },
      );

      const monthly: Record<string, MonthlyStatPoint> = {};
      for (let i = 11; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthly[key] = {
          month: d.toLocaleDateString('da-DK', { month: 'short', year: '2-digit' }),
          volume: 0,
          fees: 0,
        };
      }

      for (const txn of data.items) {
        if (txn.status !== 'succeeded') continue;
        const d = new Date(txn.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (monthly[key]) {
          monthly[key].volume += txn.amount;
          monthly[key].fees += txn.fee;
        }
      }

      return Object.values(monthly);
    },
    staleTime: 60_000,
  });
}

/**
 * Trigger a CSV export download via pay-export-csv Edge Function.
 * Returns a function that, when called, initiates the browser download.
 */
export function useExportPayTransactions() {
  return async (
    clinicId: string,
    filters: Pick<PayTransactionsFilters, 'since' | 'until' | 'status'> = {},
  ): Promise<void> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    const qs = new URLSearchParams({ clinic_id: clinicId });
    if (filters.since) qs.set('since', filters.since);
    if (filters.until) qs.set('until', filters.until);
    if (filters.status && filters.status.length > 0) qs.set('status', filters.status.join(','));

    const resp = await fetch(
      `${env.VITE_SUPABASE_URL}/functions/v1/pay-export-csv?${qs.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ''}`,
          apikey: env.VITE_SUPABASE_ANON_KEY,
        },
      },
    );

    if (!resp.ok) {
      const json = await resp.json().catch(() => null) as { error?: { message?: string } } | null;
      throw new Error(json?.error?.message ?? `CSV export failed (${resp.status})`);
    }

    const blob = await resp.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;

    const cd = resp.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^";]+)"?/.exec(cd);
    a.download = match?.[1] ?? `transaktioner-${new Date().toISOString().slice(0, 10)}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
}

// ── Formatting ────────────────────────────────────────────────────────────────

/** Format an amount in øre as a DKK currency string. */
export function formatPayMoney(ore: number, currency = 'DKK'): string {
  return (ore / 100).toLocaleString('da-DK', {
    style: 'currency',
    currency: currency.toUpperCase(),
  });
}
